import React, { useEffect, useEffectEvent } from "react";
import { ErrorMessage, isErrorMessage } from "@sscs/types";
import {
  Button,
  CopyHash,
  Dialog,
  LongTextLabel,
  QueryResult,
  Toast,
} from "@sscs/ui";
import { DownloadIcon } from "@sscs/ui/icons";
import { FileType } from "Types";
import {
  useDownloadFilestreamFileQuery,
  useDownloadVersionQuery as useDownloadVersionQuery,
} from "../../services/download.api";
import styles from "./downloadDialog.module.scss";

// Download links expire after 60 seconds
const DOWNLOAD_LINK_EXPIRY = 60;

type DownloadFileDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  fileID: string;
  fileType: FileType;
  isRepro?: boolean;
  onDownload?: () => void;
};

const DownloadDialogContent = ({
  onClose,
  fileID,
  fileType,
  isRepro,
  onDownload,
}: Omit<DownloadFileDialogProps, "isOpen">): React.JSX.Element => {
  const {
    data: fileStreamLink,
    isError: isFilestreamError,
    error: fileStreamError,
  } = useDownloadFilestreamFileQuery(
    { file_id: fileID },
    { skip: fileType !== "FILESTREAM" },
  );
  const {
    data: versionLink,
    isError: isVersionError,
    error: versionError,
  } = useDownloadVersionQuery(
    { version_id: fileID, repro: isRepro },
    { skip: fileType !== "VERSION" },
  );
  const downloadLink = fileType === "FILESTREAM" ? fileStreamLink : versionLink;
  const isError =
    fileType === "FILESTREAM" ? isFilestreamError : isVersionError;
  const registerDownload = useEffectEvent(() => onDownload?.());

  useEffect(() => {
    if (downloadLink) registerDownload();
  }, [downloadLink]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (downloadLink) {
      timeout = setTimeout(() => {
        Toast.show({
          title: `Download link for ${downloadLink.file_name} expired`,
          type: "info",
        });
        onClose();
      }, DOWNLOAD_LINK_EXPIRY * 1000);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [downloadLink, onClose]);

  // The API errors will generally be handled by rtkQueryErrorLogger, except for 401 cases,
  // so we need to handle that case here.
  useEffect(() => {
    if (isError) {
      onClose();

      let downloadError: ErrorMessage | undefined;

      if (isErrorMessage(fileStreamError)) {
        downloadError = fileStreamError;
      } else if (isErrorMessage(versionError)) {
        downloadError = versionError;
      }

      if (downloadError?.status !== 401) return;

      const errorMessage = downloadError?.data?.error ?? "Unknown";

      Toast.show({
        title: errorMessage,
        type: "error",
      });
    }
  }, [fileStreamError, isError, onClose, versionError]);

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>Download file</Dialog.Header.Title>
      </Dialog.Header>
      <Dialog.Body>
        <QueryResult status={{ status: downloadLink ? "SUCCESS" : "LOADING" }}>
          {downloadLink && (
            <div>
              <strong>
                <LongTextLabel label={downloadLink.file_name} />
              </strong>
              <div className={styles["file-hash"]}>
                <span>SHA256:</span>
                <span className={styles.hash} title={downloadLink.hash_sha256}>
                  {downloadLink.hash_sha256}
                </span>
                <CopyHash
                  id="download-file-copy-hash"
                  sha256={downloadLink.hash_sha256}
                />
              </div>
            </div>
          )}
        </QueryResult>
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Footer.Actions>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            id="file-download"
            href={downloadLink?.download_link}
            startIcon={<DownloadIcon />}
            onClick={onClose}
            disabled={!downloadLink}
            tooltipTitle={
              downloadLink ? undefined : "Preparing the download link"
            }
          >
            Download
          </Button>
        </Dialog.Footer.Actions>
      </Dialog.Footer>
    </>
  );
};

const DownloadDialog = ({
  isOpen,
  ...props
}: DownloadFileDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <DownloadDialogContent {...props} />
  </Dialog.Frame>
);

export default DownloadDialog;
